import { useState, useCallback, useEffect } from 'react'
import { gql } from 'graphql-request'
import { MessageSquare } from 'lucide-react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import debounce from 'lodash/debounce'
import { truncate } from 'lodash'

import { useQuery } from '@hooks/graphql'
import { stripMarkdown } from '@helpers/format'
import { Button } from '@components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover'
import Image from '@components/Image'

const TUTORS_QUERY = gql`
  query tutors($audience: AudienceEnum!, $search: String, $tags: [String!], $page: Int) {
    tutors(audience: $audience, search: $search, tags: $tags, page: $page, perPage: 10) {
      nodes {
        id
        name
        description
        summary
        visibility
        image {
          url
        }
      }
      pagesCount
      nodesCount
    }
  }
`

const TutorCombobox = ({ value, onChange }) => {
  const [open, setOpen] = useState(false)
  const [noTutorSelected, setNoTutorSelected] = useState(value === null)
  const [searchTerm, setSearchTerm] = useState('')

  // Create a debounced search function
  const debouncedSetSearchTerm = useCallback(
    debounce((text) => setSearchTerm(text), 300),
    []
  )

  // Fetch tutors with the current search term
  const { data: { tutors: { nodes: tutors = [] } = {} } = {}, isFetching: isSearching } = useQuery({
    queryKey: ['tutors', 'ME', searchTerm],
    gqlQuery: TUTORS_QUERY,
    variables: { 
      audience: 'ME', 
      page: 1, 
      search: searchTerm || undefined // Only include search if there's a term
    }
  })

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.cancel()
    }
  }, [debouncedSetSearchTerm])

  const formatPrompt = (description) => {
    return truncate(stripMarkdown(description), { length: 40 })
  }

  return (
    <Popover className='w-full' open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {value ? value.name : (noTutorSelected ? "No tutor" : "Select a tutor...")}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align='start'>
        <Command modal={true}>
          <CommandInput
            className='w-full'
            placeholder="Search tutors..."
            onValueChange={(search) => debouncedSetSearchTerm(search)}
          />
          <CommandList>
            <If condition={isSearching && tutors.length === 0 && searchTerm}>
              <div className="py-6 text-center text-sm">Searching...</div>
            </If>
            <CommandEmpty>
              <Choose>
                <When condition={!isSearching && searchTerm && tutors.length === 0}>
                  No tutors found for "{searchTerm}".
                </When>
                <Otherwise>
                  No tutors available.
                </Otherwise>
              </Choose>
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                key="no-tutor"
                value="no-tutor"
                onSelect={() => {
                  onChange(null)
                  setNoTutorSelected(true)
                  setOpen(false)
                }}
                className="cursor-pointer"
              >
                <div className='flex items-center w-full'>
                  <div className='leading-tight'>
                    <p className='text-sm font-medium leading-none'>No tutor</p>
                    <small className='text-xs text-gray-500'>No tutor selected</small>
                  </div>
                </div>
                <Check
                  className={twMerge(
                    "mr-2 h-4 w-4",
                    value === null ? "opacity-100" : "opacity-0"
                  )}
                />
              </CommandItem>
              {tutors.map((tutor) => (
                <CommandItem
                  key={tutor.id}
                  value={tutor.id}
                  onSelect={(selectedTutorId) => {
                    const tutorToSelect = tutors.find(t => t.id === selectedTutorId)
                    if (value?.id === selectedTutorId) {
                      onChange(null)
                      setNoTutorSelected(true)
                    } else {
                      onChange(tutorToSelect)
                      setNoTutorSelected(false)
                    }
                    setOpen(false)
                  }}
                  className="cursor-pointer"
                >
                  <div className='flex items-center w-full'>
                    <Choose>
                      <When condition={tutor.image?.url}>
                        <Image
                          src={tutor.image?.url}
                          alt={tutor.name}
                          className='size-8 rounded-md mr-2 object-cover shrink-0'
                        />
                      </When>
                      <Otherwise>
                        <div className='flex items-center justify-center size-8 rounded-md mr-2 bg-accent shrink-0'>
                          <MessageSquare className='size-4 text-gray-400' />
                        </div>
                      </Otherwise>
                    </Choose>
                    <div className='leading-tight truncate'>
                      <p className='text-sm leading-none'>{tutor.name}</p>
                      <small className='text-xs text-gray-500'>
                        <Choose>
                          <When condition={tutor.summary}>
                            {truncate(tutor.summary, { length: 40 })}
                          </When>
                          <Otherwise>
                            {formatPrompt(tutor.description)}
                          </Otherwise>
                        </Choose>
                      </small>
                    </div>
                  </div>
                  <Check
                    className={twMerge(
                      "mr-2 h-4 w-4",
                      value?.id === tutor.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default TutorCombobox